/**
 * Auth service: the ONLY module that knows how sessions are created.
 *
 * MOCK IMPLEMENTATION. Everything below is backed by localStorage so the
 * console can be clicked through before a backend exists. Nothing here is
 * secure: accounts live in this browser only.
 *
 * ── Plugging in a real provider ────────────────────────────────────────
 * Keep these five signatures and replace the bodies:
 *
 *   getSession()                    → read the provider's current session
 *   signInWithPassword(email, pw)   → provider password sign-in
 *   signInWithGoogle()              → provider OAuth redirect (returns after
 *                                     the callback route completes)
 *   signUp({ name, email, password })
 *   signOut()                       → provider sign-out + clear local state
 *
 * WorkOS AuthKit: redirect to the backend's /auth/login, handle /callback,
 *   and have getSession() call /auth/me (cookie session).
 * Supabase: supabase.auth.getSession / signInWithPassword /
 *   signInWithOAuth({ provider: 'google' }) / signUp / signOut.
 * Clerk: wrap <App> in <ClerkProvider> and back these with useClerk().
 *
 * useAuthStore and ProtectedRoute depend only on this module, so nothing
 * else in the app has to change.
 */
import { DEMO_ACCOUNT } from '@/data/mock';
import { AuthError, type Session, type User } from './types';

export { AuthError } from './types';
export type { Session, User } from './types';

const SESSION_KEY = 'latisent.session';
const USERS_KEY = 'latisent.mock-users';
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const FAKE_LATENCY_MS = 450;

interface StoredUser {
  id: string;
  name: string;
  email: string;
  /** SHA-256 of the password. Still a mock: never do this client-side for real. */
  passwordHash: string;
}

const delay = (ms = FAKE_LATENCY_MS) => new Promise((resolve) => setTimeout(resolve, ms));

async function sha256(input: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');
}

function readUsers(): StoredUser[] {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    return raw ? (JSON.parse(raw) as StoredUser[]) : [];
  } catch {
    return [];
  }
}

function writeUsers(users: StoredUser[]) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function writeSession(user: User): Session {
  const session: Session = { user, expiresAt: Date.now() + SESSION_TTL_MS };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();
const isEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** The current session, or null. Synchronous for the mock; a real provider may make this async. */
export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (!session?.user || session.expiresAt < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  await delay();
  const normalized = normalizeEmail(email);

  if (normalized === DEMO_ACCOUNT.email && password === DEMO_ACCOUNT.password) {
    return writeSession({ id: 'usr_demo', name: DEMO_ACCOUNT.name, email: DEMO_ACCOUNT.email, provider: 'password' });
  }

  const user = readUsers().find((u) => u.email === normalized);
  if (!user || user.passwordHash !== (await sha256(password))) {
    throw new AuthError('invalid_credentials', 'That email and password don’t match an account.');
  }
  return writeSession({ id: user.id, name: user.name, email: user.email, provider: 'password' });
}

/** Demo only: pretends the Google round-trip succeeded. */
export async function signInWithGoogle(): Promise<Session> {
  await delay(700);
  return writeSession({ id: 'usr_google_demo', name: 'Sam Okafor', email: 'sam.okafor@gmail.com', provider: 'google' });
}

export async function signUp(input: { name: string; email: string; password: string }): Promise<Session> {
  await delay();
  const email = normalizeEmail(input.email);
  const name = input.name.trim() || email.split('@')[0];

  if (!isEmail(email)) throw new AuthError('invalid_email', 'Enter a valid email address.');
  if (input.password.length < 8) throw new AuthError('weak_password', 'Use at least 8 characters.');

  const users = readUsers();
  if (email === DEMO_ACCOUNT.email || users.some((u) => u.email === email)) {
    throw new AuthError('email_taken', 'An account with this email already exists. Sign in instead.');
  }

  const user: StoredUser = {
    id: `usr_${crypto.randomUUID().slice(0, 8)}`,
    name,
    email,
    passwordHash: await sha256(input.password),
  };
  writeUsers([...users, user]);
  return writeSession({ id: user.id, name: user.name, email: user.email, provider: 'password' });
}

/** Demo only: a real provider would email a reset link. */
export async function requestPasswordReset(email: string): Promise<void> {
  await delay();
  if (!isEmail(normalizeEmail(email))) throw new AuthError('invalid_email', 'Enter a valid email address.');
}

export async function signOut(): Promise<void> {
  localStorage.removeItem(SESSION_KEY);
}
